<template>
    <div>
        <pc-label
            v-bind:label="recipientsColumnHeader"
            bold
            class="d-sm-none" />
        <pc-table
            id="RecipientTable"
            ref="recipientTable"
            v-bind:items="envelopeElement_recipientDefinitions"
            v-bind:columns="columns"
            key-prop="PublicId"
            empty-text="templateTableEmptyText"
            hide-search
            card-hide-sort
            card-hide-headers
            hide-card-horizontal-separator>
            <template v-slot:Name="recipientDefinition">
                <div class="d-flex">
                    <div>
                        <pc-bs-icon
                            name="person-fill"
                            class="text-primary"
                            style="font-size: 1.5rem" />
                    </div>
                    <div class="ms-2">
                        <div>{{ recipientDefinition.Label }}</div>
                        <recipient-contact-info-display
                            v-if="!contactInfoIncompleteString(recipientDefinition)"
                            v-bind:contact-info-obj="getSavedOrUpdatedContactInfo(recipientDefinition)" />
                        <envelope-status-text
                            v-if="contactInfoIncompleteString(recipientDefinition)"
                            v-bind:text="contactInfoIncompleteString(recipientDefinition)"
                            v-bind:status-severity="statusSeverity.Error"
                            italic />
                    </div>
                </div>
            </template>
            <template v-slot:Status="row">
                <envelope-status-text
                    v-if="outOfSync(row)"
                    v-bind:text="outOfSyncError"
                    v-bind:status-severity="statusSeverity.Error"
                    bold />
                <envelope-status-text
                    v-bind:text="getStatus(row)"
                    v-bind:status-severity="getRecipientStatusSeverity(row)"
                    bold />
                <div class="fw-bold">
                    {{ getStatusComment(row) }}
                </div>
            </template>
        </pc-table>
    </div>
</template>

<script>
    import Vuex from "vuex";
    import { isModuleRegistered } from "SharedStores/util";
    import { PROJECT_NAMESPACE, PROJECT_TYPES } from "SharedStores/Project/projectStore";
    import { PROJECT_FORM_NAMESPACE, PROJECT_FORM_TYPES } from "Forms/Components/ProjectForm/projectFormStore";
    import { ENVELOPE_RECIPIENT_CONTACT_INFO_TYPE } from "ESignatures/EditEnvelope/editEnvelopeStore";
    import { envelopeElementMixin } from "./envelopeElementMixin";
    import { ESIGNATURE_STATUS_SEVERITY, getRecipientStatusSeverity } from "ESignatures/eSignatureConstants";
    import { KEYS, NAMESPACE } from "../i18n/eSignature.i18n";
    import envelopeStatusText from "./envelopeStatusText.vue";
    import recipientContactInfoDisplay from "ESignatures/recipientContactInfoDisplay.vue";

    export default {
        components: {
            envelopeStatusText,
            recipientContactInfoDisplay
        },
        mixins: [envelopeElementMixin],
        props: {
            envelope: { type: Object, required: false, default: null },
            envelopeDefinition: { type: Object, required: true }
        },
        data() {
            return {
                statusSeverity: { ...ESIGNATURE_STATUS_SEVERITY }
            };
        },
        computed: {
            isProjectModuleRegistered() {
                return isModuleRegistered(PROJECT_NAMESPACE, this.$store.state);
            },
            isProjectFormModuleRegistered() {
                return isModuleRegistered(PROJECT_FORM_NAMESPACE, this.$store.state);
            },
            columns() {
                return [
                    {
                        header: this.recipientsColumnHeader,
                        property: "Name",
                        visible: true,
                        searchable: false
                    },
                    {
                        header: this.statusColumnHeader,
                        property: "Status",
                        visible: Object.values(this.recipients).some((r) => Boolean(r.Status))
                    }
                ];
            },
            recipients() {
                return this.envelope?.Recipients ?? {};
            },
            ...Vuex.mapGetters({
                dataFieldValueFromId: PROJECT_TYPES.getters.dataFieldValueFromId,
                getUserLoginIdToUserInfo: PROJECT_FORM_TYPES.getters.getUserLoginIdToUserInfo
            })
        },
        methods: {
            getUpdatedContactInfo(recipientDefinition) {
                if (!this.isProjectModuleRegistered || !this.isProjectFormModuleRegistered) {
                    return null;
                }
                let first, last, email;
                if (this.getContactInfoType(recipientDefinition) === ENVELOPE_RECIPIENT_CONTACT_INFO_TYPE.UserField) {
                    const value = this.dataFieldValueFromId(recipientDefinition.UserFieldId);
                    //If a recipient was selected via a user selector and then unselected, value will be an empty string
                    if (!value) {
                        first = null;
                        last = null;
                        email = null;
                    } else {
                        first = this.getUserLoginIdToUserInfo(value).FirstName;
                        last = this.getUserLoginIdToUserInfo(value).LastName;
                        //When recipient is selected via a user selector, email should be hidden
                        email = "";
                    }
                } else if (
                    this.getContactInfoType(recipientDefinition) ===
                    ENVELOPE_RECIPIENT_CONTACT_INFO_TYPE.IndividualFields
                ) {
                    first = this.dataFieldValueFromId(recipientDefinition.FirstNameFieldId);
                    last = this.dataFieldValueFromId(recipientDefinition.LastNameFieldId);
                    email = this.dataFieldValueFromId(recipientDefinition.EmailFieldId);
                } else {
                    first = recipientDefinition.FirstNameFixed;
                    last = recipientDefinition.LastNameFixed;
                    email = recipientDefinition.EmailFixed;
                }
                return {
                    first: first?.trim(),
                    last: last?.trim(),
                    email: email?.trim()
                };
            },
            getSavedOrUpdatedContactInfo(recipientDefinition) {
                const savedRecipient = this.getRecipientFromDefinition(recipientDefinition);
                if (savedRecipient) {
                    if (
                        this.getContactInfoType(recipientDefinition) === ENVELOPE_RECIPIENT_CONTACT_INFO_TYPE.UserField
                    ) {
                        return {
                            first: savedRecipient.FirstName,
                            last: savedRecipient.LastName,
                            email: ""
                        };
                    }
                    return {
                        first: savedRecipient.FirstName,
                        last: savedRecipient.LastName,
                        email: savedRecipient.Email
                    };
                }
                return this.getUpdatedContactInfo(recipientDefinition);
            },
            outOfSync(recipientDefinition) {
                const savedContactInfo = this.getSavedOrUpdatedContactInfo(recipientDefinition);
                const updatedContactInfo = this.getUpdatedContactInfo(recipientDefinition);
                if (recipientDefinition.contactInfoType === ENVELOPE_RECIPIENT_CONTACT_INFO_TYPE.UserField) {
                    return (
                        (savedContactInfo.first !== updatedContactInfo.first ||
                            savedContactInfo.last !== updatedContactInfo.last) &&
                        updatedContactInfo.first &&
                        updatedContactInfo.last
                    );
                }
                return (
                    (savedContactInfo.first !== updatedContactInfo.first ||
                        savedContactInfo.last !== updatedContactInfo.last ||
                        savedContactInfo.email !== updatedContactInfo.email) &&
                    updatedContactInfo.first &&
                    updatedContactInfo.last &&
                    updatedContactInfo.email
                );
            },
            // This method only checks for UserField and IndividualFields recipients
            // since there is nothing to check for Fixed Recipients
            contactInfoIncompleteString(recipientDefinition) {
                if (!this.isProjectModuleRegistered || this.envelopeElement_hasEnvelope) {
                    return null;
                }
                if (this.getContactInfoType(recipientDefinition) === ENVELOPE_RECIPIENT_CONTACT_INFO_TYPE.UserField) {
                    return this.dataFieldValueFromId(recipientDefinition.UserFieldId) ? null : this.noUserSelectedError;
                } else if (
                    this.getContactInfoType(recipientDefinition) ===
                    ENVELOPE_RECIPIENT_CONTACT_INFO_TYPE.IndividualFields
                ) {
                    const missingDataFields = [];
                    if (!this.dataFieldValueFromId(recipientDefinition.FirstNameFieldId)) {
                        missingDataFields.push(this.firstNameDataField);
                    }
                    if (!this.dataFieldValueFromId(recipientDefinition.LastNameFieldId)) {
                        missingDataFields.push(this.lastNameDataField);
                    }
                    if (!this.dataFieldValueFromId(recipientDefinition.EmailFieldId)) {
                        missingDataFields.push(this.emailDataField);
                    }
                    if (missingDataFields.length > 0) {
                        return this.getIncompleteDataLocalizedString(missingDataFields);
                    }
                    return null;
                }
            },
            getIncompleteDataLocalizedString(dataFields) {
                switch (dataFields.length) {
                    case 1:
                        return this.$app.res({
                            namespace: NAMESPACE,
                            key: KEYS.incompleteDataMissingOneDataField,
                            values: { dataField: dataFields[0] }
                        });
                    case 2:
                        return this.$app.res({
                            namespace: NAMESPACE,
                            key: KEYS.incompleteDataMissingTwoDataFields,
                            values: {
                                dataField1: dataFields[0],
                                dataField2: dataFields[1]
                            }
                        });
                    case 3:
                        return this.$app.res({
                            namespace: NAMESPACE,
                            key: KEYS.incompleteDataMissingThreeDataFields,
                            values: {
                                dataField1: dataFields[0],
                                dataField2: dataFields[1],
                                dataField3: dataFields[2]
                            }
                        });
                }
            },
            getContactInfoType(recipientDefinition) {
                if (recipientDefinition.UserFieldId) {
                    return ENVELOPE_RECIPIENT_CONTACT_INFO_TYPE.UserField;
                } else if (
                    recipientDefinition.FirstNameFixed &&
                    recipientDefinition.LastNameFixed &&
                    recipientDefinition.EmailFixed
                ) {
                    return ENVELOPE_RECIPIENT_CONTACT_INFO_TYPE.Fixed;
                }
                return ENVELOPE_RECIPIENT_CONTACT_INFO_TYPE.IndividualFields;
            },
            getRecipientFromDefinition(recipientDefinition) {
                return this.envelopeElement_recipientsByDefinitionId[recipientDefinition.PublicId];
            },
            getStatus(recipientDefinition) {
                const rec = this.getRecipientFromDefinition(recipientDefinition);
                if (!rec) {
                    return "";
                }
                return rec.Status.Display + ": " + rec.StatusTimestamp.Display;
            },
            getStatusComment(recipientDefinition) {
                const rec = this.getRecipientFromDefinition(recipientDefinition);
                if (!rec || !rec.Status.StatusComment) {
                    return "";
                }
                return '"' + rec.Status.StatusComment + '"';
            },
            getRecipientStatusSeverity(recipientDefinition) {
                const recipient = this.getRecipientFromDefinition(recipientDefinition);
                return getRecipientStatusSeverity(recipient.Status.Code);
            }
        }
    };
</script>
